import React, { useEffect, useRef, useState } from "react";

// Letter scramble animation — random chars settle into target word
// Used for action buttons & topic titles on hover/inView
const CHARS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
const SYMS = "!<>-_\\/[]{}—=+*^?#";

export const ScrambleText = ({
    children,
    trigger = "hover",   // "hover" | "inView" | "always"
    duration = 600,      // ms total
    stagger = 28,        // ms between chars settling
    className = "",
    chars = CHARS + SYMS,
    onComplete,
    keepHeight = true,
    ...rest
}) => {
    const text = typeof children === "string" ? children : "";
    const [display, setDisplay] = useState(text);
    const [hovered, setHovered] = useState(false);
    const rafRef = useRef(null);
    const startRef = useRef(0);
    const containerRef = useRef(null);

    const run = () => {
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
        startRef.current = performance.now();
        const tick = (now) => {
            const elapsed = now - startRef.current;
            const out = text
                .split("")
                .map((ch, i) => {
                    if (ch === " ") return " ";
                    const settleAt = i * stagger;
                    if (elapsed >= settleAt + 220) return ch;
                    if (elapsed < settleAt) {
                        return chars[Math.floor(Math.random() * chars.length)];
                    }
                    // In transition phase
                    const r = Math.random();
                    if (r < 0.5) return ch;
                    return chars[Math.floor(Math.random() * chars.length)];
                })
                .join("");
            setDisplay(out);
            if (elapsed < duration + text.length * stagger) {
                rafRef.current = requestAnimationFrame(tick);
            } else {
                setDisplay(text);
                onComplete && onComplete();
            }
        };
        rafRef.current = requestAnimationFrame(tick);
    };

    useEffect(() => {
        if (trigger === "always") run();
        return () => rafRef.current && cancelAnimationFrame(rafRef.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (trigger === "hover" && hovered) run();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hovered]);

    useEffect(() => {
        if (trigger !== "inView") return;
        if (!containerRef.current) return;
        const node = containerRef.current;
        const obs = new IntersectionObserver(
            ([e]) => {
                if (e.isIntersecting) {
                    run();
                    obs.disconnect();
                }
            },
            { threshold: 0.4 }
        );
        obs.observe(node);
        return () => obs.disconnect();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <span
            ref={containerRef}
            className={className}
            onMouseEnter={() => trigger === "hover" && setHovered(true)}
            onMouseLeave={() => trigger === "hover" && setHovered(false)}
            style={keepHeight ? { display: "inline-block" } : undefined}
            {...rest}
        >
            {display}
        </span>
    );
};

// Allow parent to imperatively trigger
export const ScrambleTextControlled = ({ text, active, duration = 600, stagger = 28, className = "" }) => {
    const [display, setDisplay] = useState(text);
    const rafRef = useRef(null);
    const startRef = useRef(0);

    useEffect(() => {
        if (!active) {
            setDisplay(text);
            return;
        }
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
        startRef.current = performance.now();
        const chars = CHARS + SYMS;
        const tick = (now) => {
            const elapsed = now - startRef.current;
            const out = text
                .split("")
                .map((ch, i) => {
                    if (ch === " ") return " ";
                    const settleAt = i * stagger;
                    if (elapsed >= settleAt + 220) return ch;
                    if (elapsed < settleAt) {
                        return chars[Math.floor(Math.random() * chars.length)];
                    }
                    return Math.random() < 0.5 ? ch : chars[Math.floor(Math.random() * chars.length)];
                })
                .join("");
            setDisplay(out);
            if (elapsed < duration + text.length * stagger) {
                rafRef.current = requestAnimationFrame(tick);
            } else {
                setDisplay(text);
            }
        };
        rafRef.current = requestAnimationFrame(tick);
        return () => rafRef.current && cancelAnimationFrame(rafRef.current);
    }, [active, text, duration, stagger]);

    return <span className={className}>{display}</span>;
};

export default ScrambleText;
